import { useEffect, useCallback, FC, useState, useRef } from "react";

import { Box, chakra } from "@chakra-ui/react";
import { i18n, useTranslation, Trans } from "next-i18next";
import Router from "next/router";

import { createModal } from "@components/Modal";
import { ROUTES } from "@utils/constants";

import useOrderStorage from "../storage/useOrderStorage";

const EXPIRE_TIME = 5 * 60 * 1000; // 5 minutes
const EXPIRE_NOTIFY_TIME = 1 * 60 * 1000; // 1 minute

type TMessageWithTimerProps = {
  onTimerCompleted: () => void;
};

type TModalDestroyType = {
  destroy: () => void;
};

const MessageWithTimer: FC<TMessageWithTimerProps> = ({ onTimerCompleted }) => {
  const [seconds, setSeconds] = useState(EXPIRE_NOTIFY_TIME / 1000);

  useEffect(() => {
    const intervalHandle = window.setInterval(() => {
      setSeconds((currentSeconds) => currentSeconds - 1);
    }, 1000);
    return () => {
      window.clearInterval(intervalHandle);
    };
  }, []);

  useEffect(() => {
    if (seconds === 0) {
      onTimerCompleted();
    }
  }, [onTimerCompleted, seconds]);

  return (
    <Trans
      i18n={i18n!}
      i18nKey="order-expire-message"
      values={{ seconds: seconds >= 0 ? seconds : 0 }}
      components={{ counter: <chakra.span color="red.lead" mx="0.15rem" /> }}
    />
  );
};

const useExpireOrder = (): void => {
  const { t } = useTranslation();

  const [isModalVisible, setIsModalVisible] = useState(false);

  const { clearData, updateData } = useOrderStorage();

  const timerRef = useRef<number | null>(null);
  const modalDestroyRef = useRef<TModalDestroyType | null>(null);

  const resetExpireTimer = useCallback(() => {
    updateData();
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setIsModalVisible(true);
    }, EXPIRE_TIME - EXPIRE_NOTIFY_TIME);
  }, []);

  const handleDisconnect = useCallback(() => {
    // User clicked diconnect or timer completed, redirect to landing page
    setIsModalVisible(false);
    clearData();
    Router.push(ROUTES.LANDING);
  }, [clearData]);

  const handleStayConnected = useCallback(() => {
    // User clicked stay connected, need to reset expire timer
    setIsModalVisible(false);
    resetExpireTimer();
  }, [resetExpireTimer]);

  const hideModal = useCallback(() => {
    if (modalDestroyRef.current) {
      modalDestroyRef.current.destroy();
      modalDestroyRef.current = null;
    }
  }, []);

  const showModal = useCallback(() => {
    hideModal();
    modalDestroyRef.current = createModal({
      content: (
        <Box my="1rem" fontSize="1.125rem">
          <MessageWithTimer onTimerCompleted={handleDisconnect} />
        </Box>
      ),
      hasRejectButton: true,
      hasSubmitButton: true,
      submitLabel: t("order-expire-stay_connected"),
      rejectLabel: t("order-expire-disconnect"),
      isCentered: true,
      hasHeader: false,
      hasCloseButton: false,
      onSubmit: handleStayConnected,
      onReject: handleDisconnect,
    });
  }, [handleDisconnect, handleStayConnected, hideModal, t]);

  useEffect(() => {
    if (isModalVisible) {
      showModal();
    } else {
      hideModal();
    }
  }, [isModalVisible, hideModal, showModal]);

  useEffect(() => {
    resetExpireTimer();
  }, [resetExpireTimer]);

  // On user interaction, reset expire timer
  const userInteractionCallback = useCallback(() => {
    resetExpireTimer();
  }, [resetExpireTimer]);

  useEffect(() => {
    window.document.addEventListener("click", userInteractionCallback);
    window.document.addEventListener("touchstart", userInteractionCallback);
    window.document.addEventListener("keydown", userInteractionCallback);
    return () => {
      window.document.removeEventListener("click", userInteractionCallback);
      window.document.removeEventListener("touchstart", userInteractionCallback);
      window.document.removeEventListener("keydown", userInteractionCallback);
    };
  }, [userInteractionCallback]);
};

export default useExpireOrder;
